import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Edit3, ShoppingCart, X, CheckCircle2, Euro, FileText, Download, Pencil, Truck, PackageCheck, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import PartnerBadge from "@/components/PartnerBadge";
import PaymentBadge from "@/components/PaymentBadge";
import ExportButton from "@/components/ExportButton";
import InvoiceEditor from "@/components/InvoiceEditor";
import { api } from "@/lib/api";
import { formatEuro, formatEuro2, formatDateEU, todayISO } from "@/lib/constants";

const unitPriceForCustomer = (product, partnerLevel) => {
  if (!product) return 0;
  if (product.category === "Horeca" && product.pricing_tiers) {
    if (partnerLevel === "Partner Level 2") return product.pricing_tiers.partner2 || product.pricing_tiers.standard;
    if (partnerLevel === "Partner Level 1") return product.pricing_tiers.partner1 || product.pricing_tiers.standard;
    return product.pricing_tiers.standard || 0;
  }
  return product.retail_price || 0;
};

const STATUS_STYLES = {
  Confirmed: { bg: "#F7F1E826", color: "#1F4D3A", label: "Confirmed" },
  Shipped:   { bg: "#E85D0426", color: "#E85D04", label: "Shipped" },
  Delivered: { bg: "#2A9D8F26", color: "#2A9D8F", label: "Delivered" },
  Cancelled: { bg: "#D6282826", color: "#D62828", label: "Cancelled" },
};

const StatusBadge = ({ status, testId }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.Confirmed;
  return (
    <span
      className="px-1.5 py-0.5 rounded uppercase text-[10px] font-bold tracking-wider"
      style={{ backgroundColor: s.bg, color: s.color }}
      data-testid={testId}
    >
      {s.label}
    </span>
  );
};

const blank = () => ({
  customer_id: "",
  order_date: todayISO(),
  notes: "",
  items: [],
});

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [productionBatches, setProductionBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [customerFilter, setCustomerFilter] = useState("all");
  const [invoiceOrderId, setInvoiceOrderId] = useState(null);      // opens invoice editor for this order
  const [editInvoice, setEditInvoice] = useState(null);            // opens invoice editor for this existing invoice
  const [invoicesByOrder, setInvoicesByOrder] = useState({});      // orderId -> Invoice[]

  const loadInvoices = async () => {
    try {
      const all = await api.listInvoices();
      const map = {};
      for (const inv of all) {
        if (!inv.order_id) continue;
        (map[inv.order_id] = map[inv.order_id] || []).push(inv);
      }
      setInvoicesByOrder(map);
    } catch { /* ignore */ }
  };

  const load = async () => {
    const [o, c, p, pb] = await Promise.all([
      api.listOrders(), api.listCustomers(), api.listProducts({ active: true }), api.listProductionBatches(),
    ]);
    setOrders(o);
    setCustomers(c);
    setProducts(p);
    setProductionBatches(pb);
    loadInvoices();
  };
  useEffect(() => { load(); }, []);

  const selectedCustomer = customers.find((c) => c.id === form.customer_id);
  const partnerLevel = selectedCustomer?.partner_level || "Standard";

  const itemsWithComputed = useMemo(() => {
    return form.items.map((it) => {
      const product = products.find((p) => p.id === it.product_id);
      const unit = it.unit_price !== null && it.unit_price !== undefined && it.unit_price !== ""
        ? Number(it.unit_price)
        : unitPriceForCustomer(product, partnerLevel);
      const total = (Number(it.quantity) || 0) * unit;
      return { ...it, product, _unit: unit, _total: total };
    });
  }, [form.items, products, partnerLevel]);

  const subtotal = useMemo(
    () => itemsWithComputed.reduce((a, i) => a + i._total, 0),
    [itemsWithComputed]
  );

  const filtered = useMemo(() => {
    if (customerFilter === "all") return orders;
    return orders.filter((o) => o.customer_id === customerFilter);
  }, [orders, customerFilter]);

  const openCreate = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (o) => {
    if ((o.status || "Confirmed") !== "Confirmed") {
      toast.error(`Cannot edit a ${o.status} order`);
      return;
    }
    setEditing(o);
    setForm({
      customer_id: o.customer_id,
      order_date: o.order_date || todayISO(),
      notes: o.notes || "",
      items: (o.items || []).map((it) => ({
        product_id: it.product_id,
        quantity: it.quantity,
        unit_price: it.unit_price,
      })),
    });
    setOpen(true);
  };

  const setCustomer = (v) => {
    // When customer changes, wipe manual unit_price on all items so partner-level pricing recalculates.
    setForm((f) => ({
      ...f,
      customer_id: v === "__none" ? "" : v,
      items: f.items.map((it) => ({ ...it, unit_price: null })),
    }));
  };

  const addItem = () => {
    setForm((f) => ({
      ...f,
      items: [...f.items, { product_id: "", quantity: 1, unit_price: null }],
    }));
  };
  const updateItem = (idx, patch) => {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], ...patch };
      return { ...f, items };
    });
  };
  const removeItem = (idx) => {
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!form.customer_id) return toast.error("Pick a customer");
    if (form.items.length === 0) return toast.error("Add at least one line item");
    const payload = {
      customer_id: form.customer_id,
      order_date: form.order_date || todayISO(),
      notes: form.notes,
      items: form.items
        .filter((it) => it.product_id && Number(it.quantity) > 0)
        .map((it) => ({
          product_id: it.product_id,
          quantity: Number(it.quantity),
          unit_price: it.unit_price === "" || it.unit_price === null ? null : Number(it.unit_price),
        })),
    };
    try {
      if (editing) {
        await api.updateOrder(editing.id, payload);
        toast.success("Order updated");
      } else {
        await api.createOrder(payload);
        toast.success("Order created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save order");
    }
  };

  const del = async (o) => {
    const status = o.status || "Confirmed";
    const willRestore = status === "Confirmed";
    const suffix = willRestore
      ? "\nInventory & batch stock WILL be restored."
      : `\nStatus is ${status} — inventory will NOT be restored.`;
    if (!window.confirm(`Delete order for "${o.customer_name}" on ${formatDateEU(o.order_date)}?${suffix}`)) return;
    try {
      await api.deleteOrder(o.id);
      toast.success(willRestore ? "Order deleted — inventory restored" : "Order deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const changeStatus = async (o, newStatus) => {
    const restoreNote = newStatus === "Cancelled" && (o.status || "Confirmed") === "Confirmed"
      ? "\nInventory & batch stock WILL be restored."
      : "";
    if (newStatus === "Cancelled" && !window.confirm(`Cancel order for "${o.customer_name}"?${restoreNote}`)) return;
    try {
      await api.changeOrderStatus(o.id, newStatus);
      toast.success(`Order → ${newStatus}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const markPaid = async (o) => {
    try {
      await api.markOrderPaid(o.id);
      toast.success(`Order marked paid (${formatEuro(o.subtotal)})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const registerPartial = async (o) => {
    const balance = (o.subtotal || 0) - (o.amount_paid || 0);
    const v = window.prompt(`Register payment for ${o.customer_name}\nBalance: ${formatEuro(balance)}\n\nAmount in €:`);
    const amount = parseFloat(v);
    if (!amount || amount <= 0) return;
    try {
      await api.registerPayment(o.id, amount);
      toast.success(`Payment of ${formatEuro(amount)} registered`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="page-orders">
      <PageHeader
        overline="Sales"
        title="Orders"
        subtitle="Customer order history with format-specific batch numbers and partner-tier pricing."
        actions={
          <div className="flex gap-2">
            <ExportButton module="orders" />
            <Button onClick={openCreate} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="add-order-btn">
              <Plus size={16} className="mr-2" /> New Order
            </Button>
          </div>
        }
      />

      <div className="card-soft p-4 mb-5 flex gap-3">
        <Select value={customerFilter} onValueChange={setCustomerFilter}>
          <SelectTrigger className="w-full sm:w-72 bg-white border-[#E5E5E5]" data-testid="order-customer-filter">
            <SelectValue placeholder="Filter by customer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All customers</SelectItem>
            {customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={ShoppingCart}
          title="No orders yet"
          description="Record a customer order to track revenue, batches, and partner-level pricing."
          action={
            <Button onClick={openCreate} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="empty-add-order">
              <Plus size={16} className="mr-2" /> New Order
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((o) => (
            <div key={o.id} className="card-soft card-soft-hover p-5" data-testid={`order-card-${o.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#6C757D] font-bold">
                    {formatDateEU(o.order_date)}
                  </div>
                  <h3 className="font-display font-semibold text-lg text-[#222] truncate">{o.customer_name}</h3>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <PartnerBadge level={o.partner_level_snapshot || "Standard"} testId={`order-tier-${o.id}`} />
                    <StatusBadge status={o.status || "Confirmed"} testId={`order-status-${o.id}`} />
                    {(() => {
                      const paid = Number(o.amount_paid || 0);
                      const sub = Number(o.subtotal || 0);
                      const bal = Math.max(sub - paid, 0);
                      const st = bal <= 0.001 ? "paid" : paid <= 0.001 ? "unpaid" : "partial";
                      return <PaymentBadge status={st} balance={bal} testId={`order-payment-${o.id}`} />;
                    })()}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Subtotal</div>
                  <div className="font-display font-bold text-xl text-[#E85D04]">{formatEuro(o.subtotal)}</div>
                  {Number(o.amount_paid || 0) > 0 && (
                    <div className="text-[10px] text-[#2A9D8F] font-bold">Paid {formatEuro(o.amount_paid)}</div>
                  )}
                </div>
              </div>
              <div className="mt-3 space-y-1">
                {(o.items || []).map((it, i) => (
                  <div key={i} className="text-sm bg-[#F7F1E8]/60 rounded px-3 py-1.5" data-testid={`order-item-row-${o.id}-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1 truncate">
                        <span className="font-semibold text-[#222]">{it.product_name}</span>
                        <span className="text-[#6C757D]"> × {it.quantity}</span>
                      </div>
                      <div className="font-semibold text-[#1F4D3A] ml-2">{formatEuro(it.total_price)}</div>
                    </div>
                    {Array.isArray(it.assigned_batches) && it.assigned_batches.length > 0 ? (
                      <div className="mt-1 flex flex-wrap gap-1" data-testid={`order-batches-${o.id}-${i}`}>
                        {it.assigned_batches.map((a, k) => (
                          <span key={k}
                                className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white border border-[#E5E5E5] text-[#1F4D3A]"
                                data-testid={`order-batch-chip-${o.id}-${i}-${k}`}>
                            {a.batch_id} · {a.qty}
                          </span>
                        ))}
                      </div>
                    ) : it.batch_number ? (
                      <div className="mt-1">
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white border border-[#E5E5E5] text-[#1F4D3A]">
                          {it.batch_number}
                        </span>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
              {o.notes && <div className="mt-2 text-xs text-[#222]/80 italic">{o.notes}</div>}

              {/* Invoices linked to this order */}
              {(invoicesByOrder[o.id] || []).length > 0 && (
                <div className="mt-3 border-t border-dashed border-[#E5E5E5] pt-2 space-y-1">
                  {(invoicesByOrder[o.id] || []).map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between text-xs bg-[#F7F1E8]/60 rounded px-2 py-1" data-testid={`order-invoice-${o.id}-${inv.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText size={12} className="text-[#E85D04] shrink-0" />
                        <span className="font-mono text-[#1F4D3A] font-semibold">{inv.invoice_number}</span>
                        <span
                          className="px-1.5 py-0.5 rounded uppercase text-[9px] font-bold tracking-wider"
                          style={{
                            backgroundColor: inv.status === "Paid" ? "#2A9D8F26" : inv.status === "Partially Paid" ? "#E85D0426" : inv.status === "Sent" ? "#1F4D3A26" : inv.status === "Cancelled" ? "#D6282826" : "#6C757D26",
                            color: inv.status === "Paid" ? "#2A9D8F" : inv.status === "Partially Paid" ? "#E85D04" : inv.status === "Sent" ? "#1F4D3A" : inv.status === "Cancelled" ? "#D62828" : "#6C757D",
                          }}
                          data-testid={`invoice-status-badge-${inv.id}`}
                        >
                          {inv.status}
                        </span>
                        <select
                          value={inv.status}
                          onChange={async (e) => {
                            const newStatus = e.target.value;
                            if (newStatus === inv.status) return;
                            try {
                              await api.changeInvoiceStatus(inv.id, newStatus);
                              toast.success(`${inv.invoice_number} → ${newStatus}`);
                              loadInvoices();
                              load();
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || "Failed to change status");
                            }
                          }}
                          className="ml-1 text-[10px] px-1 py-0.5 rounded bg-white border border-[#E5E5E5] text-[#222] hover:border-[#E85D04] cursor-pointer"
                          data-testid={`invoice-inline-status-${inv.id}`}
                          title="Change status"
                        >
                          <option value="Draft">Draft</option>
                          <option value="Sent">Sent</option>
                          <option value="Partially Paid">Partially Paid</option>
                          <option value="Paid">Paid</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-semibold text-[#1F4D3A]">{formatEuro2(inv.total)}</span>
                        <button className="text-[#6C757D] hover:text-[#E85D04]"
                          onClick={() => setEditInvoice(inv)}
                          data-testid={`invoice-edit-${inv.id}`} title="Edit invoice">
                          <Pencil size={13} />
                        </button>
                        <button className="text-[#6C757D] hover:text-[#1F4D3A]"
                          onClick={() => api.downloadInvoicePdf(inv.id, inv.invoice_number)}
                          data-testid={`invoice-pdf-${inv.id}`} title="Download PDF">
                          <Download size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3 flex justify-end gap-1 border-t border-[#E5E5E5] pt-3 flex-wrap">
                {(o.status || "Confirmed") === "Confirmed" && (
                  <>
                    <Button variant="ghost" size="sm"
                      onClick={() => changeStatus(o, "Shipped")}
                      className="text-[#1F4D3A] hover:bg-[#1F4D3A]/10"
                      data-testid={`ship-order-${o.id}`}>
                      <Truck size={14} className="mr-1" /> Mark Shipped
                    </Button>
                    <Button variant="ghost" size="sm"
                      onClick={() => changeStatus(o, "Cancelled")}
                      className="text-[#D62828] hover:bg-[#D62828]/10"
                      data-testid={`cancel-order-${o.id}`}>
                      <Ban size={14} className="mr-1" /> Cancel
                    </Button>
                  </>
                )}
                {(o.status || "Confirmed") === "Shipped" && (
                  <Button variant="ghost" size="sm"
                    onClick={() => changeStatus(o, "Delivered")}
                    className="text-[#2A9D8F] hover:bg-[#2A9D8F]/10"
                    data-testid={`deliver-order-${o.id}`}>
                    <PackageCheck size={14} className="mr-1" /> Mark Delivered
                  </Button>
                )}
                {(Number(o.subtotal || 0) - Number(o.amount_paid || 0)) > 0.001 && (
                  <>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => registerPartial(o)}
                      className="text-[#E76F51] hover:bg-[#E76F51]/10"
                      data-testid={`payment-partial-${o.id}`}
                    >
                      <Euro size={14} className="mr-1" /> Register Payment
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => markPaid(o)}
                      className="text-[#2A9D8F] hover:bg-[#2A9D8F]/10"
                      data-testid={`payment-mark-paid-${o.id}`}
                    >
                      <CheckCircle2 size={14} className="mr-1" /> Mark Paid
                    </Button>
                  </>
                )}
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setInvoiceOrderId(o.id)}
                  className="text-[#E85D04] hover:bg-[#E85D04]/10"
                  data-testid={`create-invoice-${o.id}`}
                >
                  <FileText size={14} className="mr-1" /> Create Invoice
                </Button>
                {(o.status || "Confirmed") === "Confirmed" && (
                  <Button variant="ghost" size="sm" onClick={() => openEdit(o)} data-testid={`edit-order-${o.id}`}>
                    <Edit3 size={16} className="mr-1" /> Edit
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => del(o)} className="text-[#E76F51] hover:bg-[#E76F51]/10" data-testid={`delete-order-${o.id}`}>
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="order-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit Order" : "New Order"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
            <Field label="Customer *" className="sm:col-span-2">
              <Select value={form.customer_id || "__none"} onValueChange={setCustomer}>
                <SelectTrigger data-testid="input-order-customer"><SelectValue placeholder="Pick a customer…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— Pick a customer —</SelectItem>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.customer_name} · {c.partner_level || "Standard"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Order Date">
              <Input type="date" value={form.order_date || ""} onChange={(e) => setForm({ ...form, order_date: e.target.value })}
                data-testid="input-order-date" />
            </Field>
          </div>

          {selectedCustomer && (
            <div className="mb-3 flex items-center gap-3 text-sm bg-[#FDEBDD]/40 rounded-lg px-4 py-2.5 border border-[#FDEBDD]" data-testid="order-pricing-applied">
              <PartnerBadge level={partnerLevel} />
              <span className="text-[#6C757D]">Pricing applied: <span className="font-semibold text-[#222]">{partnerLevel}</span> — unit prices auto-fill on product select.</span>
            </div>
          )}

          <div className="space-y-2 mb-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">Line Items *</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-order-item">
                <Plus size={14} className="mr-1" /> Add Item
              </Button>
            </div>

            {itemsWithComputed.length === 0 && (
              <div className="text-xs text-[#6C757D] bg-[#F7F1E8] rounded-lg p-3 text-center">
                No items yet — click <span className="font-semibold text-[#E85D04]">Add Item</span>.
              </div>
            )}

            {itemsWithComputed.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-start p-3 rounded-lg border border-[#E5E5E5] bg-white"
                   data-testid={`order-item-${idx}`}>
                <div className="col-span-12 sm:col-span-5">
                  <Label className="text-[10px] uppercase tracking-wider text-[#6C757D] font-semibold">Product</Label>
                  <Select value={it.product_id || "__none"} onValueChange={(v) => {
                    const pid = v === "__none" ? "" : v;
                    updateItem(idx, { product_id: pid, unit_price: null });
                  }}>
                    <SelectTrigger data-testid={`order-item-product-${idx}`}><SelectValue placeholder="Pick a product…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">— Pick —</SelectItem>
                      {products.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} · {p.category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Label className="text-[10px] uppercase tracking-wider text-[#6C757D] font-semibold">Qty</Label>
                  <Input type="number" min="0" step="0.5" value={it.quantity}
                    onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                    data-testid={`order-item-qty-${idx}`} />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Label className="text-[10px] uppercase tracking-wider text-[#6C757D] font-semibold">Unit €</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={it.unit_price ?? it._unit}
                    onChange={(e) => updateItem(idx, { unit_price: e.target.value })}
                    data-testid={`order-item-unit-${idx}`}
                  />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Label className="text-[10px] uppercase tracking-wider text-[#6C757D] font-semibold">Total</Label>
                  <div className="h-10 px-3 flex items-center font-semibold text-[#1F4D3A]" data-testid={`order-item-total-${idx}`}>
                    {formatEuro(it._total)}
                  </div>
                </div>
                <div className="col-span-11 sm:col-span-11">
                  <div className="text-[10px] text-[#6C757D] italic">
                    Batches are auto-assigned FIFO on save (oldest production first, split across multiple batches if needed).
                  </div>
                </div>
                <div className="col-span-1 flex items-end justify-end h-full">
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)}
                    className="text-[#E76F51] hover:bg-[#E76F51]/10" data-testid={`remove-order-item-${idx}`}>
                    <X size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <Field label="Notes">
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>

          <div className="flex items-center justify-between mt-3 px-3 py-2.5 bg-[#F7F1E8] rounded-lg">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#1F4D3A] font-bold">Order Subtotal</div>
            <div className="font-display font-bold text-2xl text-[#E85D04]" data-testid="order-subtotal">{formatEuro(subtotal)}</div>
          </div>

          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-order">Cancel</Button>
            <Button onClick={save} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="save-order">
              {editing ? "Save Changes" : "Create Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InvoiceEditor
        open={!!invoiceOrderId}
        orderId={invoiceOrderId}
        onClose={() => { setInvoiceOrderId(null); loadInvoices(); load(); }}
        onSaved={() => { loadInvoices(); load(); }}
      />

      <InvoiceEditor
        open={!!editInvoice}
        existingInvoice={editInvoice}
        onClose={() => { setEditInvoice(null); loadInvoices(); load(); }}
        onSaved={() => { loadInvoices(); load(); }}
      />
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">{label}</Label>
    {children}
  </div>
);
